/**
 * Single source of truth for serializing Organization (Client) Settings.
 *
 * Mirrors `serializeCardSettings.js` and `serializeLocationSettings.js`. See
 * those files' doc-blocks for the rationale. Same INVARIANT applies:
 *
 *   Both the snapshot (built from the API organization object) AND the live
 *   form state MUST be produced by THIS exact same function. Adding a new
 *   field = ONE row in FIELDS — both sides automatically stay in sync.
 *
 * Replaces parallel raw-object comparison logic in `AdminClients.jsx`.
 */

// ── Coercion helpers (same buckets as the other serializers) ──

const _toStr = (v, d = '') => (v === null || v === undefined ? d : String(v).trim());

// ── Field registry — ONE place ──
//
// Each entry: { key, org, state, type, default }
//   key:     output JSON key
//   org:     field name on the API organization object
//   state:   field name on the React form state
//   type:    coercion bucket
//   default: value when the source is missing/null/invalid
const FIELDS = [
    { key: 'name',          org: 'name',          state: 'name',          type: 'str', default: '' },
    { key: 'displayName',   org: 'display_name',  state: 'display_name',  type: 'str', default: '' },
    { key: 'contactEmail',  org: 'contact_email', state: 'contact_email', type: 'str', default: '' },
    { key: 'phone',         org: 'phone',         state: 'phone',         type: 'str', default: '' },
    { key: 'address',       org: 'address',       state: 'address',       type: 'str', default: '' },
    { key: 'brandId',       org: 'brand_id',      state: 'brand_id',      type: 'str', default: '' },
];

function normalize(v, type, def) {
    switch (type) {
        case 'str': return _toStr(v, def);
        default:    return v ?? def;
    }
}

/**
 * Serialize either an organization (from the API) or a flat form-state
 * object to a stable JSON string.
 *
 * @param {Object} input               Org object OR formData
 * @param {'org'|'state'} source       Tells the function which key style to read.
 * @returns {string} JSON string suitable for === comparison.
 */
export function serializeOrganizationSettings(input, source = 'org') {
    if (!input || typeof input !== 'object') return JSON.stringify({});

    // P_PORTAL_SAVE_INTEGRITY_AUDIT — dev-time coverage guard.
    // Warns when a `state` bag is missing a FIELDS key, which would
    // make dirty-detection invisible to the user and Save Changes
    // never enable.
    if (
        source === 'state'
        && typeof process !== 'undefined'
        && process.env
        && process.env.NODE_ENV !== 'production'
    ) {
        if (!serializeOrganizationSettings._warned) serializeOrganizationSettings._warned = new Set();
        for (const f of FIELDS) {
            if (!Object.prototype.hasOwnProperty.call(input, f.state)
                && !serializeOrganizationSettings._warned.has(f.state)) {
                serializeOrganizationSettings._warned.add(f.state);
                // eslint-disable-next-line no-console
                console.error(
                    `[SAVE_INTEGRITY] serializeOrganizationSettings(state) missing key: "${f.state}". ` +
                    'Save Changes will never enable for this field.'
                );
            }
        }
    }

    const out = {};
    for (const f of FIELDS) {
        const raw = source === 'org' ? input[f.org] : input[f.state];
        out[f.key] = normalize(raw, f.type, f.default);
    }
    return JSON.stringify(out);
}

/** Convenience export — list of canonical keys. */
export const ORGANIZATION_SETTING_KEYS = FIELDS.map(f => f.key);

/** State-bag key names (snake_case, as written into React form state). */
export const ORGANIZATION_STATE_KEYS = FIELDS.map(f => f.state);
